import { useEffect, useState } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import FreePlayPage from "pages/FreePlayPage/FreePlayPage";
import ManageLikesPage from "pages/ManageLikesPage/ManageLikesPage";
import SongsPage from "pages/SongsPage/SongsPage";
import SongDetailPage from "pages/SongDetailPage/SongDetailPage";
import DebugPage from "pages/DebugPage/DebugPage";
import Authorization from "./Authorization";
import AppHeader from "layout/AppHeader";
import AppFooter from "layout/AppFooter";
import MobileLegalLinks from "layout/MobileLegalLinks";
import SpotifyBar from "layout/SpotifyBar/SpotifyBar";
import { ErrorBoundary } from "components/ErrorBoundary";
import { REDIRECT_URL } from "./routes";
import PrivacyPage from "pages/PrivacyPage/PrivacyPage";
import TermsPage from "pages/TermsPage/TermsPage";
import AcceptTermsPage from "pages/AcceptTermsPage/AcceptTermsPage";

const scopes = [
  "user-read-private", // needed for subscription level and Web Playback SDK
  "user-read-email", // needed for Web Playback SDK level
  "streaming", // needed for Web Playback SDK
  "user-read-playback-state",
  "user-modify-playback-state",
  "playlist-read-private", // needed to import private playlist
  "playlist-read-collaborative", // needed to import collaborative playlists
  "user-library-read", // needed to read favorites
  "user-library-modify", // needed to 'favorite' a song
  "playlist-modify-private", // needed to create prviate playlists
  "playlist-modify-public", // needed to create public playlists
];

const SPOTIFY_AUTH_URL = "https://accounts.spotify.com/authorize";
const CLIENT_ID = "b4954d9c424d4d93bcb1001b6ff59baf";

function getWindowDimensions(): { width: number; height: number } {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}

function getAuthorizeHref(): string {
  const url = `${SPOTIFY_AUTH_URL}?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(
    REDIRECT_URL
  )}&scope=${scopes.join("%20")}&response_type=code`;

  return url;
}

export function AppRouter(): JSX.Element {
  const location = useLocation();
  const { width } = useWindowDimensions();

  function render(): JSX.Element {
    return (
      <div className="">
        <Authorization />
        {/* header / scrollable content / player / footer — the player sits at
            the bottom so the app bar can stay a single compact row. */}
        <div className="h-screen w-screen bg-bg flex flex-col overflow-hidden">
          <AppHeader loginUrl={getAuthorizeHref()} url={location.pathname} />
          <main className="flex-grow min-h-0 overflow-y-auto bg-bg flex flex-col">
            <div className="w-full flex flex-grow">
              <Routes>
                <Route path="/songs" element={<SongsPage />} />
                <Route path="/songs/:gid" element={<SongDetailPage />} />
                <Route
                  path="/play"
                  element={<FreePlayPage includeScore={width >= 450} />}
                />
                <Route path="/likes" element={<ManageLikesPage />} />
                <Route path="/" element={<Navigate to="/songs" />} />
                <Route path="/debug" element={<DebugPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/accept_terms" element={<AcceptTermsPage />} />
              </Routes>
            </div>
            <MobileLegalLinks />
          </main>
          {/* Third-party player code: if it crashes, keep the rest of the app. */}
          <ErrorBoundary label="The player" compact>
            <SpotifyBar />
          </ErrorBoundary>
          <AppFooter />
        </div>
      </div>
    );
  }

  return render();
}
